import type { MRT_ColumnDef } from 'material-react-table';
import { useMemo, useState } from 'react';

import {
  emptyCellOneLevel,
  formatCurrencyCell,
  formatDateWithTimeCell,
} from '@/shared';
import { useUpdateTransaccion } from '@/shared/actions/erp';
import {
  ModalObservacionComponente,
  SingleIconButton,
} from '@/shared/components/ui';
import { Contrato, Transaccion } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';
import { ViewClienteRubroInfoTableCell } from '../components/tables/rubros';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
type UseColumnsTransaccionesClienteProps = {
  contract: Contrato;
};

export const useColumnsTransaccionesCliente = ({
  contract,
}: UseColumnsTransaccionesClienteProps) => {
  ///* local state -----------------------
  const [currentRubroRef, setCurrentRubroRef] = useState<string>('');

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* mutations -----------------------
  const updTransaccion = useUpdateTransaccion({
    customSuccessMessage: `Pago reversado con éxito para el rubro ${currentRubroRef}`,
    enableRedirect: false,
  });

  ///* columns -----------------------
  const columnsTransaccion = useMemo<MRT_ColumnDef<Transaccion>[]>(
    () => [
      {
        accessorKey: 'rubro_num_ref',
        header: 'N° Rubro',
        size: 222,
        Cell: ({ row }) => {
          return (
            <ViewClienteRubroInfoTableCell
              rubro={row.original?.rubro_data!}
              contract={contract!}
            />
          );
        },
      },

      {
        accessorKey: 'fecha_pago',
        header: 'Fecha de Pago',
        size: 200,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_pago'),
        filterVariant: 'date',
        enableSorting: false,
      },

      {
        accessorKey: 'total_pagado',
        header: 'Total Pagado',
        size: 180,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'total_pagado');
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'comision',
        header: 'Comisión',
        size: 180,
        Cell: ({ row }) => {
          return formatCurrencyCell(row, 'comision');
        },
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: 'saldo_monto',
        header: 'Saldo',
        size: 210,
        Cell: ({ row }) => formatCurrencyCell(row, 'saldo_monto'),
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: 'numero_referencia',
        header: 'N° Transacción',
        size: 222,
        Cell: ({ row }) => emptyCellOneLevel(row, 'numero_referencia'),
      },

      {
        accessorKey: 'forma_pago',
        header: 'Forma de Pago',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'forma_pago'),
      },

      {
        accessorKey: 'observaciones',
        header: 'Observaciones',
        size: 200,
        Cell: ({ row }) => {
          const firstObs = row.original?.observaciones[0];

          return <ModalObservacionComponente observacion_cliente={firstObs!} />;
        },
      },

      ///* custom actions
      {
        accessorKey: 'custom_actions',
        header: 'Acciones',
        size: 90,
        Cell: ({ row }) => {
          const isAlreadyReversed = !!row.original?.estado_reverso;

          return (
            <>
              {isAlreadyReversed ? (
                <>N/A</>
              ) : (
                <SingleIconButton
                  startIcon={<RestartAltIcon />}
                  color="inherit"
                  label="Reversar Pago"
                  onClick={() => {
                    setCurrentRubroRef(
                      row.original?.rubro_data?.numero_referencia!
                    );

                    setConfirmDialog({
                      isOpen: true,
                      title: 'Reversar Pago',
                      subtitle: `¿Está seguro de reversar el pago?`,
                      onConfirm: async () => {
                        await updTransaccion.mutateAsync({
                          id: row.original.id_transaccion!,
                          data: { estado_reverso: true },
                        });

                        setConfirmDialogIsOpen(false);
                      },
                      onClose: () => {
                        setConfirmDialogIsOpen(false);
                      },
                    });
                  }}
                  noThemeColor
                />
              )}
            </>
          );
        },
      },
    ],
    []
  );

  return { columnsTransaccion };
};
